import React, { useCallback, useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RequestItem } from '@innowise-group/core';
import { Button } from '@innowise-group/mui-kit';
import { Stepper } from '@shared-mui-components';

import { ConditionsSectionFields, MainSectionFields, RequestStepsEnum, steps } from '../../request-form.data';
import { RequestFormViewProps } from '../../request-form.types';
import { CandidateLocationSection } from '../candidate-location-section';
import { CommentSection } from '../comment-section';
import { ConditionsSection } from '../conditions-section';
import { EmploymentLocationSection } from '../employment-location-section';
import { GeneralSection } from '../general-section';
import { LanguagesSection } from '../languages-section';
import { SalarySection } from '../salary-section';
import * as Styled from './mobile-view.styles';

const MobileView: React.FC<RequestFormViewProps> = ({
  onSubmit,
  handleClose,
  isNonBillableWarningToBeShown,
  ...props
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<RequestStepsEnum>(RequestStepsEnum.Main);
  const { handleSubmit, trigger } = useFormContext<RequestItem>();
  const { disabled } = props;

  const handleNextStep = useCallback(
    (fields: FieldPath<RequestItem>[]) => async () => {
      const isValidated = await trigger(fields);
      if (isValidated) {
        setActiveStep((prev) => prev + 1);
      }
    },
    [trigger],
  );

  const handlePrevStep = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  return (
    <Styled.FormContainer>
      {isNonBillableWarningToBeShown ? (
        <Styled.NonBillableWarning>
          <Styled.AlertIcon type="u_alert_circle_filled" viewBox="0 0 24 24" isPointer />
          {t('pages.requests.nonBillableRequestWillGoApproval')}
        </Styled.NonBillableWarning>
      ) : null}
      <Stepper steps={steps} activeStep={activeStep} />
      {activeStep === RequestStepsEnum.Main ? (
        <Styled.StepContainer>
          <GeneralSection {...props} />
          <EmploymentLocationSection {...props} />
          <Styled.ButtonsSection>
            <Styled.GrayButton onClick={handleClose} variant="outlined">
              {t('buttons.cancel')}
            </Styled.GrayButton>
            <Button onClick={handleNextStep(MainSectionFields)}>{t('buttons.next')}</Button>
          </Styled.ButtonsSection>
        </Styled.StepContainer>
      ) : null}
      {activeStep === RequestStepsEnum.Optional ? (
        <Styled.StepContainer>
          <CandidateLocationSection />
          <LanguagesSection />
          <SalarySection disabled={disabled} />
          <ConditionsSection {...props} />
          <Styled.ButtonsSection>
            <Styled.GrayButton onClick={handlePrevStep} variant="outlined">
              {t('buttons.back')}
            </Styled.GrayButton>
            <Button onClick={handleNextStep(ConditionsSectionFields)}>{t('buttons.next')}</Button>
          </Styled.ButtonsSection>
        </Styled.StepContainer>
      ) : null}
      {activeStep === RequestStepsEnum.Comments ? (
        <Styled.StepContainer>
          <CommentSection disabled={disabled} />
          <Styled.ButtonsSection>
            <Styled.GrayButton onClick={handlePrevStep} variant="outlined">
              {t('buttons.back')}
            </Styled.GrayButton>
            <Button onClick={handleSubmit(onSubmit)}>{t('buttons.save')}</Button>
          </Styled.ButtonsSection>
        </Styled.StepContainer>
      ) : null}
    </Styled.FormContainer>
  );
};

export default MobileView;
